import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Table } from 'src/model/table.model';

@Injectable({
  providedIn: 'root',
})
export class DatabaseService {
  dbName = environment.database.name;

  constructor() {}

  initDB(dbVersion: number): IDBOpenDBRequest {
    const request = indexedDB.open(this.dbName, dbVersion);

    request.onerror = function (event) {
      console.log("Errore durante l'apertura del database");
    };

    request.onblocked = () => {
      console.warn('IndexedDB upgrade bloccato: chiudere le connessioni aperte');
    };

    request.onsuccess = () => {
      const db = request.result;

      db.onversionchange = () => {
        console.warn('Version change rilevato, chiusura DB');
        db.close();
      };
    };

    return request;
  }

  async getDbVersion(): Promise<number> {
    let version = 0;
    await indexedDB.databases().then(databasesInfo => {
      if (databasesInfo.length > 0) {
        databasesInfo.forEach(dbInfo => {
          if (dbInfo.name === this.dbName) {
            version = dbInfo.version || 0;
          }
        });
      }
    });
    return new Promise(resolve => {
      resolve(version);
    });
  }

  saveObject(obj: any, tableName: string, dbVersion: number): Promise<any> {
    const request = this.initDB(dbVersion);
    return new Promise(function (resolve) {
      request.onsuccess = event => {
        const db = request.result;
        const tx = db.transaction(tableName, 'readwrite');
        const objStore = tx.objectStore(tableName);
        const operation = objStore.add(obj);
        operation.onerror = event => {
          console.log('ERROR ON SAVE');
        };
        operation.onsuccess = event => {
          return resolve(operation.result);
        };
        tx.oncomplete = () => {
          db.close();
        };
      };
    });
  }

  updateObject(obj: any, tableName: string, dbVersion: number): Promise<any> {
    const request = this.initDB(dbVersion);
    return new Promise(function (resolve) {
      request.onsuccess = event => {
        const db = request.result;
        const tx = db.transaction(tableName, 'readwrite');
        const objStore = tx.objectStore(tableName);
        const operation = objStore.put(obj);
        operation.onerror = event => {
          console.log('ERROR ON SAVE');
        };
        operation.onsuccess = event => {
          return resolve(operation.result);
        };
        tx.oncomplete = () => {
          db.close();
        };
      };
    });
  }

  count(tableName: string, dbVersion: number): Promise<number> {
    const request = this.initDB(dbVersion);
    return new Promise(function (resolve) {
      request.onsuccess = event => {
        const db = request.result;
        const tx = db.transaction(tableName, 'readwrite');
        const objStore = tx.objectStore(tableName);
        const operation = objStore.count();
        operation.onerror = event => {
          console.log('ERROR ON SAVE');
        };
        operation.onsuccess = event => {
          return resolve(operation.result);
        };
        tx.oncomplete = () => {
          db.close();
        };
      };
    });
  }

  createTable(table: Table, dbVersion: number): void {
    const request = this.initDB(dbVersion + 1);
    request.onupgradeneeded = event => {
      const db = request.result;
      const store = db.createObjectStore(table.name, { keyPath: 'id', autoIncrement: table.autoId });
      table.uniqueFields.forEach(field => {
        store.createIndex(field, field, { unique: true });
      });
      table.notUniqueFields.forEach(field => {
        store.createIndex(field, field, { unique: false });
      });
      // db.close();
    };
  }

  createTables(tables: Table[], dbVersion: number): void {
    const request = this.initDB(dbVersion + 1);
    request.onupgradeneeded = event => {
      const db = request.result;
      tables.forEach(table => {
        const store = db.createObjectStore(table.name, { keyPath: 'id', autoIncrement: table.autoId });
        table.uniqueFields.forEach(field => {
          store.createIndex(field, field, { unique: true });
        });
        table.notUniqueFields.forEach(field => {
          store.createIndex(field, field, { unique: false });
        });
      });
      // db.close();
    };
  }

  deleteTable(tableName: string, dbVersion: number): void {
    const request = this.initDB(dbVersion + 1);
    request.onupgradeneeded = event => {
      const db = request.result;
      db.deleteObjectStore(tableName);
      // db.close();
    };
  }

  deleteTables(tables: Table[], dbVersion: number): void {
    const request = this.initDB(dbVersion + 1);
    request.onupgradeneeded = event => {
      const db = request.result;
      tables.forEach(table => {
        if (db.objectStoreNames.contains(table.name)) {
          db.deleteObjectStore(table.name);
        }
      });
      // db.close();
    };
  }

  resetDB(tablesToRemove: Table[], tablesToAdd: Table[], dbVersion: number): void {
    const request = indexedDB.open(this.dbName, dbVersion + 1);

    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;

      tablesToRemove.forEach(table => {
        if (db.objectStoreNames.contains(table.name)) {
          db.deleteObjectStore(table.name);
        }
      });

      tablesToAdd.forEach(table => {
        const store = db.createObjectStore(table.name, {
          keyPath: 'id',
          autoIncrement: table.autoId,
        });

        table.uniqueFields.forEach(field => {
          store.createIndex(field, field, { unique: true });
        });

        table.notUniqueFields.forEach(field => {
          store.createIndex(field, field, { unique: false });
        });
      });
    };

    request.onsuccess = () => {
      request.result.close();
    };
  }

  findAll(tableName: string, dbVersion: number): Promise<any[]> {
    const request = this.initDB(dbVersion);
    return new Promise(function (resolve) {
      request.onsuccess = event => {
        const db = request.result;
        const tx = db.transaction(tableName, 'readwrite');
        const objStore = tx.objectStore(tableName);
        const items = objStore.getAll();
        items.onsuccess = () => {
          return resolve(items.result);
        };
        items.onerror = () => {
          console.log('Error 2');
        };
        tx.oncomplete = () => {
          db.close();
        };
      };
    });
  }

  getFirstElement(tableName: string, dbVersion: number): Promise<any> {
    const request = this.initDB(dbVersion);
    return new Promise(function (resolve) {
      request.onsuccess = event => {
        const db = request.result;
        const tx = db.transaction(tableName, 'readwrite');
        const objStore = tx.objectStore(tableName);
        const items = objStore.getAll();
        items.onsuccess = () => {
          return resolve(items.result[0]);
        };
        items.onerror = () => {
          console.log('Error 2');
        };
        tx.oncomplete = () => {
          db.close();
        };
      };
    });
  }

  findOne(id: number, tableName: string, dbVersion: number): Promise<any> {
    const request = this.initDB(dbVersion);
    return new Promise(function (resolve) {
      request.onsuccess = event => {
        const db = request.result;
        const tx = db.transaction(tableName, 'readwrite');
        const objStore = tx.objectStore(tableName);
        const item = objStore.get(id);
        item.onsuccess = () => {
          return resolve(item.result);
        };
        item.onerror = () => {
          console.log('Error 2');
        };
        tx.oncomplete = () => {
          db.close();
        };
      };
    });
  }

  findOneByKey(value: any, indexName: string, tableName: string, dbVersion: number): Promise<any> {
    const request = this.initDB(dbVersion);
    return new Promise(function (resolve) {
      request.onsuccess = event => {
        const db = request.result;
        const tx = db.transaction(tableName, 'readwrite');
        const objStore = tx.objectStore(tableName);
        const index = objStore.index(indexName);
        const item = index.openCursor(value);
        item.onsuccess = () => {
          if (item.result) {
            return resolve(item.result.value);
          } else {
            return resolve(null);
          }
        };
        item.onerror = () => {
          console.log('Error 2');
        };
        tx.oncomplete = () => {
          db.close();
        };
      };
    });
  }

  findByKey(value: any, indexName: string, tableName: string, dbVersion: number): Promise<any> {
    const request = this.initDB(dbVersion);
    return new Promise(function (resolve) {
      request.onsuccess = event => {
        const db = request.result;
        const tx = db.transaction(tableName, 'readwrite');
        const objStore = tx.objectStore(tableName);
        const index = objStore.index(indexName);
        const item = index.getAll(value);
        item.onsuccess = () => {
          return resolve(item.result);
        };
        item.onerror = () => {
          console.log('Error 2');
        };
        tx.oncomplete = () => {
          db.close();
        };
      };
    });
  }

  delete(id: number, tableName: string, dbVersion: number): Promise<boolean> {
    const request = this.initDB(dbVersion);
    return new Promise(function (resolve) {
      request.onsuccess = event => {
        const db = request.result;
        const tx = db.transaction(tableName, 'readwrite');
        const objStore = tx.objectStore(tableName);
        const res = objStore.delete(id);
        res.onsuccess = () => {
          return resolve(true);
        };
        res.onerror = () => {
          console.log('Error 2');
        };
        tx.oncomplete = () => {
          db.close();
        };
      };
    });
  }

  async clearTable(table: Table, dbVersion: number): Promise<boolean> {
    const request = this.initDB(dbVersion);
    return new Promise(function (resolve) {
      request.onsuccess = event => {
        const db = request.result;
        if (db.objectStoreNames.contains(table.name)) {
          const tx = db.transaction(table.name, 'readwrite');
          const objStore = tx.objectStore(table.name);
          const objectStoreRequest = objStore.clear();
          objectStoreRequest.onsuccess = event => {
            return resolve(true);
          };
          objectStoreRequest.onerror = event => {
            console.log('Error 2');
          };
          tx.oncomplete = () => {
            db.close();
          };
        } else {
          return resolve(false);
        }
      };
    });
  }

  getTableObject(tableName: string): Table {
    const tables: Table[] = environment.database.tables;
    let table: Table = {
      name: '',
      autoId: false,
      uniqueFields: [],
      notUniqueFields: [],
    };
    for (let i = 0; i < tables.length; i++) {
      if (tables[i].name === tableName) {
        table = tables[i];
        break;
      }
    }
    return table;
  }
}
